import React, { useState } from 'react';
import { useModal } from 'react-modal-hook';
import { Field, FieldProps, Form, Formik } from 'formik';
import styled, { css } from 'styled-components';
import * as Yup from 'yup';
import { AlertContent, Button, CentBasedInputCurrency, ErrorText, H4, Label, ModalFactory, ModalFooter, ModalHeader, ModalTitle, Text } from 'components';
import { theme, toRem } from 'design';
import { ICheck, IPOSTicketTotals } from 'types';
import { centsToDollarsDisplay } from 'utils/currency';
import { getDateTimeWithoutZone } from 'utils/date';

const MAX_TIP_CASH = 500000;

interface IEditCheckAmountsProps {
  check: ICheck;
  timeZone: string;
  onSave(check: ICheck): void;
  onClose(): void;
}

const Layout = styled.section<{ $hidden?: boolean }>`
  position: relative;
  z-index: ${theme.zIndex.modal + 2};
  flex-flow: column nowrap;
  justify-content: flex-start;
  align-items: flex-start;
  width: 100%;
  max-width: ${toRem(640)};
  height: auto;
  margin: ${toRem(theme.layout.gutter.large)} 0;
  padding: ${toRem(theme.modal.spacing.large)};
  border-radius: ${toRem(22)};
  background-color: ${theme.color.white};
  box-shadow: ${theme.shadow.modal};

  ${({ $hidden }) =>
    $hidden &&
    css`
      display: none;

      &:first-of-type + div {
        display: none;
      }
    `}
`;

const ModalSubTitle = styled(ModalTitle)`
  margin-top: ${toRem(16)};
  font-size: ${toRem(16)};

  &.date {
    margin-top: ${toRem(8)};
    font-size: ${toRem(12)};
    color: ${theme.color.neutral[600]};
    font-weight: ${theme.font.weight.regular};
  }
`;

const ModalBody = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${toRem(8)};
  width: 100%;
  height: auto;
  border-radius: ${toRem(11)};
  background-color: ${theme.color.neutral[50]};
  justify-items: center;
  border: 1px solid ${theme.color.neutral[200]};

  > * {
    margin-bottom: 0 !important;
  }
`;

const TopRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-gap: ${toRem(16)};
  height: auto;
  width: 100%;
  border-radius: ${toRem(11)} ${toRem(11)} 0 0;
  background-color: ${theme.color.neutral[100]};
  padding: ${toRem(16)};
  border-bottom: 1px solid ${theme.color.neutral[200]};

  @media (min-width: ${theme.breakpoint.med}px) {
    grid-template-columns: 1fr 1fr 1fr 1fr;
    grid-gap: ${toRem(8)};
  }
`;

const TopRowItem = styled.div`
  display: flex;
  flex-flow: column;
  align-items: center;

  & * {
    font-family: ${theme.font.family.euclid};
    font-weight: ${theme.font.weight.regular};
  }

  & > p {
    // required to override existing specificity
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.medium};
    text-align: center;
    word-break: break-word;

    &.check-time {
      font-size: ${toRem(11)};
      font-weight: ${theme.font.weight.regular};
      color: ${theme.color.neutral[600]};
    }
  }

  & > label {
    // required to override existing specificity
    margin-bottom: ${toRem(8)};
    color: ${theme.color.neutral[500]};
    font-weight: ${theme.font.weight.medium};
    font-size: ${toRem(11)};

    &.display-id {
      position: relative;
      top: ${toRem(4)};
      font-size: ${toRem(16)};
      font-weight: ${theme.font.weight.bold};
      color: ${theme.color.neutral[900]};
    }
  }

  & > h4 {
    // required to override existing specificity
    margin-bottom: ${toRem(12)};
    font-weight: ${theme.font.weight.bold};
  }
`;

const StyledButton = styled(Button)`
  width: 100%;
`;

const FormWrapper = styled.div`
  align-items: start;
  background-color: ${theme.color.neutral[50]};
  display: flex;
  flex-flow: column nowrap;
  gap: ${toRem(8)};
  padding: ${toRem(16)};
  width: 100%;
  border-radius: 0 0 ${toRem(11)} ${toRem(11)};

  label {
    color: ${theme.color.neutral[500]};
  }
`;

const FormSection = styled.section`
  display: flex;
  flex-flow: column nowrap;
  padding: ${toRem(6)} 0;
  gap: ${toRem(6)};
  width: 100%;

  h4 {
    font-size: ${toRem(14)};
    font-weight: ${theme.font.weight.semiBold};
    margin-bottom: ${toRem(6)};
  }
`;

const FormInputs = styled.div`
  display: flex;
  flex-flow: row nowrap;
  width: 100%;
  gap: ${toRem(6)};
`;

const InputContainer = styled.div`
  display: flex;
  flex-flow: column nowrap;
  width: 100%;
  gap: ${toRem(6)};

  label {
    margin-bottom: ${toRem(8)};
    font-size: ${toRem(12)};
  }

  .error {
    input {
      border: 1px solid ${theme.color.danger.medium};
    }
  }
`;

const TotalWrapper = styled.div`
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(6)};
  padding: ${toRem(6)} 0;

  label {
    font-size: ${toRem(12)};
  }
`;

export const EditCheckAmounts: React.FC<IEditCheckAmountsProps> = ({ check, timeZone, onClose, onSave }) => {
  const [isDirtyCancel, setIsDirtyCancel] = useState(false);

  const setInitialValues = (): IPOSTicketTotals => ({ ...check.totals });

  const validationSchema = Yup.object().shape({
    gross: Yup.number().required(),
    net: Yup.number().required(),
    tip: Yup.object().shape({
      gross: Yup.number().required(),
      net: Yup.number().required(),
      cash: Yup.number()
        .required()
        .typeError('Invalid value')
        .test('maxTipCashValue', `Maximum is ${centsToDollarsDisplay(MAX_TIP_CASH)}`, (value) => !(value > MAX_TIP_CASH)),
      creditCard: Yup.number().required(),
      creditCardFee: Yup.number().required(),
    }),
    autoGratuity: Yup.object().shape({
      gross: Yup.number().required(),
      net: Yup.number().required(),
      cash: Yup.number().required(),
      creditCard: Yup.number().required(),
      creditCardFee: Yup.number().required(),
    }),
  });

  const handleSubmit = (values: any) => {
    // Recalculate tip totals
    const tipTotals = {
      ...values.tip,
      gross: values.tip.creditCard + values.tip.cash,
      net: values.tip.creditCard + values.tip.cash - values.tip.creditCardFee,
    };

    // Recalculate check totals
    const totals = {
      ...values,
      gross: tipTotals.gross + values.autoGratuity.gross,
      net: tipTotals.net + values.autoGratuity.net,
      tip: tipTotals,
    };

    onSave({ ...check, totals });
  };

  const handleCancel = (dirty: boolean) => {
    setIsDirtyCancel(dirty);
    if (dirty) {
      showCancelModal();
    } else {
      onClose();
    }
  };

  const [showCancelModal, hideCancelModal] = useModal(
    () => (
      <ModalFactory
        customModal
        onClose={() => {
          setIsDirtyCancel(false);
          hideCancelModal();
        }}
        isActive
        closeOnOutSideClick={false}
        hideCloseButton
      >
        <AlertContent
          title="Cancel Changes?"
          message="Do you want to cancel the changes you’ve made to this check?"
          leftActionLabel="Go Back"
          rightActionLabel="Cancel Changes"
          onLeftAction={() => {
            setIsDirtyCancel(false);
            hideCancelModal();
          }}
          onRightAction={() => {
            hideCancelModal();
            onClose();
          }}
          rightActionAppearance="destructive"
        />
      </ModalFactory>
    ),
    [onClose]
  );

  return (
    <ModalFactory customModal onClose={onClose} isActive closeOnOutSideClick={false} hideCloseButton>
      <Layout $hidden={isDirtyCancel} data-cy="edit-check-amounts-modal">
        <Formik initialValues={setInitialValues()} onSubmit={handleSubmit} validationSchema={validationSchema}>
          {({ dirty, isValid, values, setFieldValue }) => (
            <Form>
              <ModalHeader data-cy="split-check-modal-header">
                <ModalTitle>Edit Amounts</ModalTitle>
                <ModalSubTitle>Check #{check.displayId}</ModalSubTitle>
                <ModalSubTitle className="date">{getDateTimeWithoutZone(check.processingTime, timeZone)}</ModalSubTitle>
              </ModalHeader>
              <ModalBody>
                <TopRow data-cy="split-check-modal-check-info">
                  <TopRowItem data-cy="day-segment">
                    <Label>Day Segment</Label>
                    <Text>{check.segmentName}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="employee">
                    <Label>Employee</Label>
                    <Text>{check.employeeName}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="tip">
                    <Label>Tips & Autograts</Label>
                    <Text>{centsToDollarsDisplay(check.totals.gross)}</Text>
                  </TopRowItem>
                  <TopRowItem data-cy="total">
                    <Label>Total</Label>
                    <Text>{centsToDollarsDisplay(check.gross)}</Text>
                  </TopRowItem>
                </TopRow>
                <FormWrapper data-cy="edit-check-amounts-modal-form">
                  <FormSection>
                    <H4>Tips</H4>
                    <FormInputs>
                      <InputContainer>
                        <Label>Credit Card</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-tip-cc" value={check.totals.tip.creditCard?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                      <InputContainer>
                        <Field name="tip.cash">
                          {({ field, meta }: FieldProps) => (
                            <>
                              <Label>Cash</Label>
                              <CentBasedInputCurrency
                                {...field}
                                data-cy="edit-check-amounts-modal-form-input-tip-cash"
                                value={values.tip.cash?.toString()}
                                onChange={(value: string) => setFieldValue('tip.cash', parseInt(value, 10))}
                                allowZero
                                className={meta.touched && meta.error ? 'error' : ''}
                              />
                              {meta.touched && meta.error && (
                                <ErrorText size="xxsmall" data-cy="edit-check-amounts-modal-form-input-tip-cash-error">
                                  {meta.error}
                                </ErrorText>
                              )}
                            </>
                          )}
                        </Field>
                      </InputContainer>
                    </FormInputs>
                    <TotalWrapper>
                      <Label>Total:</Label>
                      <Text size="small">{centsToDollarsDisplay(values.tip.creditCard + values.tip.cash)}</Text>
                    </TotalWrapper>
                  </FormSection>
                  <FormSection>
                    <H4>Autograts</H4>
                    <FormInputs>
                      <InputContainer>
                        <Label>Credit Card</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-auto-gratuity-cc" value={check.totals.autoGratuity.creditCard?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                      <InputContainer>
                        <Label>Cash</Label>
                        <CentBasedInputCurrency data-cy="edit-check-amounts-modal-form-input-auto-gratuity-cash" value={check.totals.autoGratuity.cash?.toString()} onChange={() => null} disabled allowZero />
                      </InputContainer>
                    </FormInputs>
                    <TotalWrapper>
                      <Label>Total:</Label>
                      <Text size="small">{centsToDollarsDisplay(values.autoGratuity.creditCard + values.autoGratuity.cash)}</Text>
                    </TotalWrapper>
                  </FormSection>
                </FormWrapper>
              </ModalBody>
              <ModalFooter columns={2} data-cy="split-check-modal-footer">
                <StyledButton data-cy="cancel-btn" label="Cancel" appearance="white" size="large" onClick={() => handleCancel(dirty)} />
                <StyledButton data-cy="save-btn" label="Save" disabled={!(isValid && dirty)} isProcessing={false} type="submit" appearance="primary" size="large" />
              </ModalFooter>
            </Form>
          )}
        </Formik>
      </Layout>
    </ModalFactory>
  );
};
